/**
 * Инициализация локализации веб-клиента.
 *
 * Назначение файла: собрать экземпляр i18next из общего пакета @hive/i18n и отдать его
 * React через провайдер. Второго каталога текстов в клиенте нет и быть не должно
 * (docs/adr/0005-i18n-foundation.md).
 *
 * Импорты: createI18n и реестр локалей из @hive/i18n.
 *
 * Локаль выбирается по языку браузера, но пока включён только русский, поэтому
 * resolveLocale вернёт «ru» для любого языка — это ожидаемо (§19.2, волны запуска).
 */

import { createI18n, resolveLocale, textDirection, type LocaleCode } from "@hive/i18n";
import type { i18n as I18nInstance } from "i18next";

/** Определяет язык по настройкам браузера с учётом включённых локалей. */
export function detectLocale(): LocaleCode {
  const preferred = typeof navigator === "undefined" ? [] : [...navigator.languages];
  return resolveLocale(preferred);
}

/**
 * Готовит i18next и выставляет язык и направление письма на элементе html.
 * Направление ставится здесь, а не в компонентах: атрибут dir влияет на всю вёрстку,
 * и для арабского это единственная точка переключения (§19.9).
 */
export async function setupI18n(): Promise<{ i18n: I18nInstance; locale: LocaleCode }> {
  const locale = detectLocale();
  const i18n = await createI18n(locale, { debug: import.meta.env.DEV });

  const root = document.documentElement;
  root.lang = locale;
  root.dir = textDirection(locale);

  return { i18n, locale };
}
